import React, {Component} from "react"
import {Row} from "react-bootstrap"
import PropTypes from "prop-types"

import {Select} from "@irati/ui/src/FormItem"
import mainTheme from "@irati/ui/src/global/theme"
import {H6, Body} from "@irati/ui/src/Typography"
import FormattedMessage, {injectIntl, InjectIntl} from "@irati/utils/lang"
import Flex from "@irati/ui/src/Flex"
import adminReportingService from "@irati/adminReporting/services/adminReporting"
import authentication from "@irati/auth"
import {
  CelebrationsReportCategory,
  CelebrationsReportDuration,
  CelebrationsReportUser,
  CelebrationsReportTeam} from "@irati/types"
import FilterChipToggle from "@irati/ui/src/FilterChipToggle"
import {IconTypes} from "@irati/ui/src/Icon"
import Loading from "@irati/ui/src/Loading"
import adminTracking, {ViewType, Filter, Product} from "@irati-tracking/admin/admin"
import {RouterLink} from "@irati/ui/src/Link"

import RecognitionCard from "../RecognitionCard"
import Region, {ALL_TEAMS} from "../../Region"
import {EmptyData} from "../../EmptyData"
import Download from "../Download"
import ShareLink from "../../../ShareLink"

interface Props extends InjectIntl {
  isTeams?: boolean
}

interface Option {
  value: string,
  label: string
}

interface State {
  regionId: string,
  timeRangeOptions: Option[],
  duration: CelebrationsReportDuration,
  category: CelebrationsReportCategory,
  showFilters: boolean,
  load: Promise<void> | null,
  teams: CelebrationsReportTeam[],
  users: CelebrationsReportUser[]
}

class CelebratedPeople extends Component<Props, State> {
  static contextTypes = {
    apps: PropTypes.object.isRequired
  }

  state = {
    regionId: ALL_TEAMS,
    timeRangeOptions: [],
    duration: CelebrationsReportDuration.CelebrationsLast_30Days,
    category: CelebrationsReportCategory.RecognitionReceiving,
    showFilters: true,
    users: [],
    teams: [],
    load: null
  }

  componentDidMount(): void {
    const {formatMessage} = this.props

    const timeRangeOptions = [
      {label: formatMessage({id: "admin.reporting.orgSummary.timeRangeOptions.last30Days"}),
        value: CelebrationsReportDuration.CelebrationsLast_30Days},
      {label: formatMessage({id: "admin.reporting.orgSummary.timeRangeOptions.last90Days"}),
        value: CelebrationsReportDuration.CelebrationsLast_90Days},
      {label: formatMessage({id: "admin.reporting.orgSummary.timeRangeOptions.last6Months"}),
        value: CelebrationsReportDuration.CelebrationsLast_6Months},
      {label: formatMessage({id: "admin.reporting.orgSummary.timeRangeOptions.last12Months"}),
        value: CelebrationsReportDuration.CelebrationsLast_12Months}
    ]

    this.setState({timeRangeOptions})
    this.getCelebrationReport()
  }

  getCelebrationReport = () => {
    const {regionId, duration, category} = this.state
    const regionIds = regionId !== ALL_TEAMS ? [regionId] : undefined

    const load = adminReportingService.getCelebrationsReport({
      businessId: authentication.business().uuid,
      regionIds,
      duration,
      category,
      limit: 5}).then(({recognition}) => {
      this.setState({users: recognition.users, teams: recognition.teams})
    })
    this.setState({load})
  }

  handleFilteredTrackEvent = (type: string, filter?: Filter, action?: string) => {
    return adminTracking.reporting({
      source: "Navigation",
      type,
      viewType: ViewType.filtered,
      filter,
      product: this.state.category === CelebrationsReportCategory.RecognitionReceiving
        ? Product.recognitionReceived : Product.recognitionGiven,
      action})
  }

  handleUpdateRegion = (regionId: string) => {
    const {isTeams} = this.props
    if (isTeams) {
      this.handleFilteredTrackEvent("celebration_teams", Filter.team)
    } else {
      this.handleFilteredTrackEvent("celebration_people", Filter.team)
    }
    this.setState({regionId}, this.getCelebrationReport)
  }

  handleSetTimeRangeUpdate = (duration: string) => {
    const {isTeams} = this.props
    if (isTeams) {
      this.handleFilteredTrackEvent("celebration_teams", Filter.date)
    } else {
      this.handleFilteredTrackEvent("celebration_people", Filter.date)
    }
    this.setState({duration}, this.getCelebrationReport)
  }

  handleToggleFilters = () => {
    this.setState((prevState: State) => {
      return {showFilters: !prevState.showFilters}
    })
  }

  handleCategoryTrackEvent = (type: string, product: Product) => {
    return adminTracking.reporting({
      source: "Navigation",
      type,
      viewType: ViewType.filtered,
      product})
  }

  handleReceivingCategoryUpdate = () => {
    const {isTeams} = this.props
    if (isTeams) {
      this.handleCategoryTrackEvent("celebration_teams", Product.recognitionReceived)
    } else {
      this.handleCategoryTrackEvent("celebration_people", Product.recognitionReceived)
    }
    this.setState({category: CelebrationsReportCategory.RecognitionReceiving}, this.getCelebrationReport)
  }

  handleGivenCategoryUpdate = () => {
    const {isTeams} = this.props
    if (isTeams) {
      this.handleCategoryTrackEvent("celebration_teams", Product.recognitionGiven)
    } else {
      this.handleCategoryTrackEvent("celebration_people", Product.recognitionGiven)
    }
    this.setState({category: CelebrationsReportCategory.RecognitionGiving}, this.getCelebrationReport)
  }

  handleDownloadTrackEvent = () => {
    const {isTeams} = this.props
    if (isTeams) {
      this.handleFilteredTrackEvent("celebration_teams", undefined, "downloadReport")
    } else {
      this.handleFilteredTrackEvent("celebration_people", undefined, "downloadReport")
    }
  }

  getBodyText = () => {
    const {isTeams} = this.props
    const {category} = this.state
    if (isTeams) {
      if (category === CelebrationsReportCategory.RecognitionReceiving) {
        return "admin.reporting.celebrations.teams.receiving.text"
      }
      return "admin.reporting.celebrations.teams.given.text"
    }
    if (category === CelebrationsReportCategory.RecognitionReceiving) {
      return "admin.reporting.celebrations.people.receiving.text"
    }
    return "admin.reporting.celebrations.people.given.text"
  }

  render() {
    const {formatMessage, isTeams} = this.props
    const {apps} = this.context
    const {regionId, timeRangeOptions, duration, category, showFilters, load, teams, users} = this.state
    const titleText = isTeams ? "admin.reporting.celebrations.teams.title" : "admin.reporting.celebrations.people.title"
    const bodyText = this.getBodyText()

    return (
      <>
        <Flex justifyContent="space-between" marginBottom={3}>
          <div>
            <H6 marginBottom>
              <FormattedMessage id={titleText} />
            </H6>
            <Body small>
              <FormattedMessage id={bodyText} />
            </Body>
          </div>
          <Flex direction="column">
            <Flex alignItems="center" justifyContent="flex-end" gap={mainTheme.spacing.two}>
              <ShareLink />
              <div>
                <FilterChipToggle
                  id="adminSection_reportingTab_recognitionReceived_button"
                  isToggled={category === CelebrationsReportCategory.RecognitionReceiving}
                  onClick={this.handleReceivingCategoryUpdate}>
                  {formatMessage({id: "admin.reporting.celebrations.receiving.toggle"})}
                </FilterChipToggle>
                <FilterChipToggle
                  id="adminSection_reportingTab_recognitionGiven_button"
                  isToggled={category === CelebrationsReportCategory.RecognitionGiving}
                  onClick={this.handleGivenCategoryUpdate}>
                  {formatMessage({id: "admin.reporting.celebrations.given.toggle"})}
                </FilterChipToggle>
              </div>
              <FilterChipToggle isToggled={showFilters} icon={IconTypes.filter3} onClick={this.handleToggleFilters} />
            </Flex>
            {showFilters &&
              <Flex alignItems="center" gap={mainTheme.spacing.oneAndHalf} justifyContent="flex-end" marginTop={1}>
                <Region
                  id="adminSection_celebrations_country"
                  value={regionId}
                  onChange={this.handleUpdateRegion} />
                <Row>
                  <Select
                    id="adminSection_celebrations_timePeriod"
                    name="timeRangeMostPopularPerks"
                    options={timeRangeOptions}
                    value={duration}
                    onChange={this.handleSetTimeRangeUpdate} />
                </Row>
              </Flex>
            }
          </Flex>
        </Flex>
        <Loading load={load}>
          <Flex gap={mainTheme.spacing.two} marginBottom={3} wrap="wrap">
            {isTeams
              ? teams.map((team, index) => <RecognitionCard
                topCelebratedTeam={team}
                isReceived={category === CelebrationsReportCategory.RecognitionReceiving}
                index={index} /> )
              : users.map((user, index) => <RecognitionCard
                topCelebratedUser={user}
                isReceived={category === CelebrationsReportCategory.RecognitionReceiving}
                index={index} />)}
          </Flex>
          {(isTeams && teams.length === 0 || !isTeams && users.length === 0) &&
          <EmptyData text="admin.reporting.celebrations.empty.text">
            <RouterLink to="/" app={apps.RECOGNITION}>
              <FormattedMessage id="admin.reporting.celebrations.empty.link" />
            </RouterLink>
          </EmptyData>}
          <Download
            isTeams={isTeams}
            category={category}
            duration={duration}
            regionId={regionId}
            onDownload={this.handleDownloadTrackEvent} />
        </Loading>
      </>
    )
  }
}

export default injectIntl(CelebratedPeople)
